import {
  NAME_PROBLEM_TEXT,
  STORAGE_KIND_HINT,
  STORAGE_LABEL,
  type StorageKind,
  type StoragePlace,
  validateStorageName,
} from "@larder/shared";
import { useState } from "react";

const KINDS: readonly StorageKind[] = [
  "fridge",
  "freezer",
  "pantry",
  "counter",
  "cool_dark",
  // Not everything in a home goes off. A bookshelf is a real place people want
  // to remember, and it has no shelf life to model.
  "shelf",
  "other",
];

/**
 * Managing a household's storage places.
 *
 * A real kitchen has "the cupboard by the window", not "pantry". The name is
 * free text so it can say whatever makes the place findable.
 *
 * The KIND still has to be picked, and the copy says why: shelf life depends on
 * the conditions, not the label. Two cupboards can have different names and the
 * same kind; a "cupboard" that is actually a fridge would give bad advice, and
 * bad advice causes exactly the waste this app exists to prevent.
 */
export function StoragePlaces({
  places,
  onAdd,
  onRemove,
  busy,
}: {
  places: StoragePlace[];
  onAdd: (name: string, kind: StorageKind) => void;
  onRemove: (id: string) => void;
  busy: boolean;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<StorageKind>("pantry");
  const [touched, setTouched] = useState(false);

  const problem = validateStorageName(name);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (problem) return;
    onAdd(name, kind);
    setName("");
    setTouched(false);
  }

  return (
    <>
      <h3>
        Where things go <span className="count">{places.length}</span>
      </h3>
      <p className="note">
        Name them however you actually think of them — "Cupboard (green)", "Top shelf,
        left".
      </p>

      <div className="place-list">
        {places.map((p) => (
          <div key={p.id} className="place-item" data-testid={`storage-item-${p.name}`}>
            <span className="place-item-main">
              <span className="item-name">{p.name}</span>
              <span className="item-meta">{STORAGE_KIND_HINT[p.kind]}</span>
            </span>
            {/* An icon, not a word. "Remove" repeated down a list shouts at the
                user about the one action they least want to take, and it is the
                widest thing in every row. The label survives for screen readers,
                where it is still the only way to know what the button does. */}
            <button
              type="button"
              className="iconbtn danger"
              onClick={() => onRemove(p.id)}
              disabled={busy}
              aria-label={`Remove ${p.name}`}
              data-testid={`remove-storage-${p.name}`}
            >
              <TrashIcon />
            </button>
          </div>
        ))}
        {places.length === 0 && <p className="note">No spots yet.</p>}
      </div>

      <form onSubmit={submit} className="add-storage">
        <label className="field">
          <span className="label">Add a spot</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Cupboard (green)"
            autoComplete="off"
            enterKeyHint="done"
            data-testid="storage-name"
          />
        </label>

        <span className="label">What is it like in there?</span>
        <p className="note">This sets how long things last — the name doesn't.</p>
        <div className="chips">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className={`chip ${kind === k ? "on" : ""}`}
              onClick={() => setKind(k)}
              data-testid={`kind-${k}`}
            >
              {STORAGE_LABEL[k]}
            </button>
          ))}
        </div>

        {touched && problem && (
          <p className="hint" data-testid="storage-name-error">
            {NAME_PROBLEM_TEXT[problem]}
          </p>
        )}

        <button
          type="submit"
          className="chip wide"
          disabled={busy}
          data-testid="add-storage"
        >
          + Add this spot
        </button>
      </form>
    </>
  );
}

/** Heroicons `trash`, outline. Inline so it inherits `currentColor`. */
function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
      />
    </svg>
  );
}
